import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import IMarketingChannels from '../../../_interfaces/IMarketingChannels';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { MarketingChannelsService } from '../../../_services/marketing-channels/marketing-channels.service';
import { SessionService } from '../../../_services/session/session.service';
import { BaseComponent } from '../../base/base.component';
import { AdminStatePanelComponent } from '../../admin-ui/admin-state-panel/admin-state-panel.component';
import { AdminTableActionsComponent } from '../../admin-ui/admin-table-actions/admin-table-actions.component';
import { AdminListToolbarComponent } from '../../admin-ui/admin-list-toolbar/admin-list-toolbar.component';

@Component({
  standalone: true,
  selector: 'app-table-marketing-channels',
  templateUrl: './table-marketing-channels.component.html',
  styleUrls: ['./table-marketing-channels.component.scss'],
  imports: [
    CommonModule,
    TableModule,
    TranslatePipe,
    ButtonModule,
    AdminStatePanelComponent,
    AdminTableActionsComponent,
    AdminListToolbarComponent,
  ],
})
export class TableMarketingChannelsComponent extends BaseComponent implements OnInit {
  public marketingChannels: IMarketingChannels[] = [];
  public filteredMarketingChannels: IMarketingChannels[] = [];
  public searchValue = '';

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly errorsService: ErrorsService,
    private readonly translate: TranslateService,
    private readonly marketingChannelsService: MarketingChannelsService,
    public readonly session: SessionService,
    public readonly router: Router,
  ) {
    super(session);
  }

  onSearch(value: string): void {
    this.searchValue = value;
    const query = value.trim().toLowerCase();
    if (!query) {
      this.filteredMarketingChannels = [...this.marketingChannels];
      return;
    }

    this.filteredMarketingChannels = this.marketingChannels.filter((channel) =>
      (channel.name || '').toLowerCase().includes(query),
    );
  }

  load(): void {
    this.marketingChannelsService.getAll(this.customers_id).subscribe({
      next: (marketingChannels: IMarketingChannels[]) => {
        this.marketingChannels = marketingChannels;
        this.onSearch(this.searchValue);
      },
      error: (error: any) => {
        const code = this.errorsService.getMessageFromMarketingChannelError(error);
        this.messageService.add({
          summary: this.translate.instant('MESSAGES.MARKETING.CHANNELS.ERROR.SUMMARY'),
          detail: this.translate.instant(`MESSAGES.MARKETING.CHANNELS.ERROR.${code}`),
          severity: 'error',
        });
      },
    });
  }

  confirmDelete(marketingChannel: IMarketingChannels): void {
    if (!marketingChannel?.id) {
      return;
    }

    this.confirmationService.confirm({
      header: this.translate.instant('ADMIN.MARKETING.CHANNELS.CONFIRM.DELETE.TITLE', {
        marketingChannel: marketingChannel.name,
      }),
      message: this.translate.instant('ADMIN.MARKETING.CHANNELS.CONFIRM.DELETE.TEXT', {
        marketingChannel: marketingChannel.name,
      }),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.delete(marketingChannel),
    });
  }

  private delete(marketingChannel: IMarketingChannels): void {
    if (!marketingChannel?.id) {
      return;
    }

    this.marketingChannelsService.delete(this.customers_id, marketingChannel.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(
            'MESSAGES.SUCCESS.DELETE.MARKETING_CHANNELS.SUMMARY',
          ),
          detail: this.translate.instant(
            'MESSAGES.SUCCESS.DELETE.MARKETING_CHANNELS.DETAIL',
          ),
        });
        this.load();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('MESSAGES.COMMON.500.SUMMARY'),
          detail: this.translate.instant('MESSAGES.COMMON.500.DETAIL'),
        });
      },
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.load();
  }
}
